import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Heart, Pin, Lock, Clock, Eye, MessageSquare,
  Send, Trash2, ShieldBan
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function ForumThread() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyContent, setReplyContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);
  const replyRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadThread = async () => {
    try {
      const [threadRes, repliesRes] = await Promise.all([
        axios.get(`${API}/api/forum/threads/${threadId}`, { headers }),
        axios.get(`${API}/api/forum/threads/${threadId}/replies`, { headers }),
      ]);
      setThread(threadRes.data);
      setReplies(repliesRes.data.replies);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) navigate('/forum');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadThread(); }, [threadId]);

  const toggleLikeThread = async () => {
    try {
      const res = await axios.post(`${API}/api/forum/threads/${threadId}/like`, {}, { headers });
      setThread(prev => ({ ...prev, liked: res.data.liked, like_count: prev.like_count + (res.data.liked ? 1 : -1) }));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleLikeReply = async (replyId) => {
    try {
      const res = await axios.post(`${API}/api/forum/replies/${replyId}/like`, {}, { headers });
      setReplies(prev => prev.map(r => r.id === replyId ? {
        ...r, liked: res.data.liked, like_count: r.like_count + (res.data.liked ? 1 : -1)
      } : r));
    } catch (err) {
      console.error(err);
    }
  };

  const submitReply = async () => {
    if (!replyContent.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/api/forum/threads/${threadId}/replies`, {
        content: replyContent.trim()
      }, { headers });
      setReplies(prev => [...prev, res.data.reply]);
      setThread(prev => ({ ...prev, reply_count: prev.reply_count + 1 }));
      setReplyContent('');
      toast.success('Reply posted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post reply');
    } finally {
      setPosting(false);
    }
  };

  const deleteReply = async (replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await axios.delete(`${API}/api/forum/replies/${replyId}`, { headers });
      setReplies(prev => prev.filter(r => r.id !== replyId));
      setThread(prev => ({ ...prev, reply_count: Math.max(0, prev.reply_count - 1) }));
      toast.success('Reply deleted');
    } catch (err) {
      toast.error('Failed');
    }
  };

  const banUser = async (userId, name) => {
    if (!window.confirm(`Ban ${name} from the forum?`)) return;
    try {
      const res = await axios.post(`${API}/api/forum/users/${userId}/ban`, { reason: 'Moderation' }, { headers });
      toast.success(res.data.banned ? `${name} banned` : `${name} unbanned`);
    } catch (err) {
      toast.error('Failed');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!thread) return null;

  return (
    <Layout>
      <div className="space-y-5 max-w-4xl mx-auto" data-testid="forum-thread-page">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/forum/category/${thread.category_id}`)} data-testid="back-to-category" className="shrink-0 h-8 w-8 p-0 mt-0.5">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {thread.pinned && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]"><Pin className="w-3 h-3 mr-0.5" />Pinned</Badge>}
              {thread.locked && <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]"><Lock className="w-3 h-3 mr-0.5" />Locked</Badge>}
              <h1 className="text-lg sm:text-2xl font-bold break-words">{thread.title}</h1>
            </div>
          </div>
        </div>

        {/* Original Post */}
        <Card className="border border-emerald-100 bg-emerald-50/20" data-testid="thread-op">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                  {thread.author_name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-sm">{thread.author_name}</p>
                  <p className="text-[10px] text-gray-400">{timeAgo(thread.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{thread.view_count}</span>
                <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{thread.reply_count}</span>
              </div>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed break-words">{thread.content}</div>
            <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100">
              <button onClick={toggleLikeThread} className={`flex items-center gap-1 text-sm transition-colors ${thread.liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`} data-testid="like-thread">
                <Heart className={`w-4 h-4 ${thread.liked ? 'fill-current' : ''}`} />{thread.like_count}
              </button>
              {isAdmin && thread.author_id !== user?.id && (
                <button onClick={() => banUser(thread.author_id, thread.author_name)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500" title="Ban user">
                  <ShieldBan className="w-3.5 h-3.5" />Ban
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Replies */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-500">{replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}</h3>
          {replies.map((reply, i) => (
            <Card key={reply.id} className="border border-gray-100" data-testid={`reply-${reply.id}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-bold shrink-0">
                      {reply.author_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="font-medium text-sm truncate">{reply.author_name}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">{timeAgo(reply.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggleLikeReply(reply.id)} className={`flex items-center gap-1 text-xs transition-colors ${reply.liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`} data-testid={`like-reply-${reply.id}`}>
                      <Heart className={`w-3.5 h-3.5 ${reply.liked ? 'fill-current' : ''}`} />{reply.like_count}
                    </button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => deleteReply(reply.id)} title="Delete reply">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        {reply.author_id !== user?.id && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500" onClick={() => banUser(reply.author_id, reply.author_name)} title="Ban user">
                            <ShieldBan className="w-3 h-3" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{reply.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Reply Input */}
        {thread.locked ? (
          <div className="text-center py-6 text-sm text-gray-400 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" /> This thread is locked
          </div>
        ) : (
          <Card className="border border-gray-200" data-testid="reply-form">
            <CardContent className="p-3 sm:p-4">
              <Textarea
                ref={replyRef}
                placeholder="Write a reply... Use @Name to mention someone"
                rows={3}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                data-testid="reply-input"
              />
              <div className="flex justify-end mt-2">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={submitReply}
                  disabled={posting || !replyContent.trim()}
                  data-testid="submit-reply-btn"
                >
                  <Send className="w-4 h-4 mr-1" />{posting ? 'Posting...' : 'Reply'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
